import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Alert, Spinner, Form, Nav, Tab } from 'react-bootstrap';
import { FaChartLine, FaMoneyBillWave, FaExchangeAlt, FaCalculator, FaUsers, FaDownload, FaCalendarAlt, FaFilter } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const AdminLogDashboard = () => {
    const { currentUser } = useAuth();
    const [activeTab, setActiveTab] = useState('price-changes');
    
    // State Management
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [logs, setLogs] = useState([]);
    const [statistics, setStatistics] = useState({});
    
    // Filter States
    const [dateRange, setDateRange] = useState({
        startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 7 gün önce
        endDate: new Date().toISOString().split('T')[0] // bugün
    });
    const [selectedUser, setSelectedUser] = useState('');
    const [users, setUsers] = useState([]);

    // Admin kontrolü
    useEffect(() => {
        if (!currentUser || currentUser.userType !== 'admin') {
            return;
        }
        
        fetchLogs();
        fetchStatistics();
        fetchUsers();
    }, [currentUser, activeTab, dateRange, selectedUser]);

    // Fetch logs based on active tab
    const fetchLogs = useCallback(async () => {
        if (!currentUser || currentUser.userType !== 'admin') return;
        
        setLoading(true);
        setError('');

        try {
            let endpoint = '/logs/';
            const params = new URLSearchParams({
                startDate: dateRange.startDate,
                endDate: dateRange.endDate,
                limit: '50'
            });

            if (selectedUser) {
                params.append('userId', selectedUser);
            }

            switch (activeTab) {
                case 'price-changes':
                    endpoint += `price-changes?${params}`;
                    break;
                case 'status-changes':
                    endpoint += `status-changes?${params}`;
                    break;
                case 'expense-logs':
                    endpoint += `expense-logs?${params}`;
                    break;
                case 'user-activity':
                    endpoint += `user-activity?${params}`;
                    break;
                default:
                    endpoint += `all?${params}`;
            }

            const response = await api.get(endpoint);
         
            setLogs(response.data.logs || []);
        } catch (err) {
            console.error('❌ Log fetch error:', err);
            setError(err.response?.data?.message || 'Loglar yüklenirken hata oluştu');
        } finally {
            setLoading(false);
        }
    }, [currentUser, activeTab, dateRange, selectedUser]);

    // Fetch statistics
    const fetchStatistics = useCallback(async () => {
        if (!currentUser || currentUser.userType !== 'admin') return;

        try {
            const params = new URLSearchParams({
                startDate: dateRange.startDate,
                endDate: dateRange.endDate
            });

            const response = await api.get(`/logs/statistics?${params}`);
            setStatistics(response.data.statistics || {});
        } catch (err) {
            console.error('❌ Statistics fetch error:', err);
        }
    }, [currentUser, dateRange]);

    // Fetch users for filter
    const fetchUsers = useCallback(async () => {
        if (!currentUser || currentUser.userType !== 'admin') return;

        try {
            const response = await api.get('/logs/users');

            setUsers(response.data.users || []);
        } catch (err) {
            console.error('❌ Users fetch error:', err);
        }
    }, [currentUser]);

    // CSV Export
    const handleExportCSV = async () => {
        try {
            const params = new URLSearchParams({
                startDate: dateRange.startDate,
                endDate: dateRange.endDate,
                type: activeTab
            });

            if (selectedUser) {
                params.append('userId', selectedUser);
            }

            const response = await api.get(`/logs/csv-export?${params}`, {
                responseType: 'blob'
            });

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `logs-${activeTab}-${dateRange.startDate}-${dateRange.endDate}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setError('CSV export sırasında hata oluştu');
        }
    };

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    // Format date
    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleString('tr-TR');
    };

    // Get action type badge
    const getActionBadge = (actionType) => {
        const badges = {
            'PURCHASE_PRICE_CHANGE': { bg: 'warning', text: 'Alış Fiyatı' },
            'SALE_PRICE_CHANGE': { bg: 'info', text: 'Satış Fiyatı' },
            'STATUS_CHANGE': { bg: 'primary', text: 'Durum Değişikliği' },
            'EXPENSE_ADD': { bg: 'success', text: 'Masraf Eklendi' },
            'EXPENSE_DELETE': { bg: 'danger', text: 'Masraf Silindi' },
            'VEHICLE_CREATE': { bg: 'secondary', text: 'Araç Eklendi' }
        };

        const badge = badges[actionType] || { bg: 'secondary', text: actionType };
        return <Badge bg={badge.bg}>{badge.text}</Badge>;
    };

    // Admin kontrolü
    if (!currentUser || currentUser.userType !== 'admin') {
        return (
            <Container className="py-5">
                <Alert variant="danger" className="text-center">
                    <FaUsers className="me-2" />
                    <strong>Yetkisiz Erişim</strong>
                    <p className="mb-0 mt-2">Bu sayfaya sadece admin kullanıcıları erişebilir.</p>
                </Alert>
            </Container>
        );
    }

    return (
        <Container className="py-5">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h1 className="mb-2">
                        <FaChartLine className="me-2 text-primary" />
                        İşlem Logları Dashboard
                    </h1>
                    <p className="text-muted mb-0">
                        Sistem üzerindeki tüm değişiklikleri ve kullanıcı aktivitelerini görüntüleyin
                    </p>
                </div>
                <div className="d-flex gap-2">
                    <Button variant="outline-success" onClick={handleExportCSV}>
                        <FaDownload className="me-1" />
                        CSV İndir
                    </Button>
                </div>
            </div>

            {/* Filters */}
            <Card className="mb-4">
                <Card.Body>
                    <Row className="align-items-end">
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>
                                    <FaCalendarAlt className="me-1" />
                                    Başlangıç Tarihi
                                </Form.Label>
                                <Form.Control
                                    type="date"
                                    value={dateRange.startDate}
                                    onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>
                                    <FaCalendarAlt className="me-1" />
                                    Bitiş Tarihi
                                </Form.Label>
                                <Form.Control
                                    type="date"
                                    value={dateRange.endDate}
                                    onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>
                                    <FaFilter className="me-1" />
                                    Kullanıcı Filtresi
                                </Form.Label>
                                <Form.Select
                                    value={selectedUser}
                                    onChange={(e) => setSelectedUser(e.target.value)}
                                >
                                    <option value="">Tüm Kullanıcılar</option>
                                    {users.map(user => (
                                        <option key={user._id} value={user._id}>
                                            {user.name} ({user.userType})
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Button variant="primary" onClick={fetchLogs} className="w-100">
                                <FaFilter className="me-1" />
                                Filtrele
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Statistics Cards */}
            <Row className="mb-4">
                <Col md={3}>
                    <Card className="text-center">
                        <Card.Body>
                            <FaMoneyBillWave className="text-warning mb-2" size={30} />
                            <h4>{statistics.totalPriceChanges || 0}</h4>
                            <small className="text-muted">Fiyat Değişikliği</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center">
                        <Card.Body>
                            <FaExchangeAlt className="text-info mb-2" size={30} />
                            <h4>{statistics.totalStatusChanges || 0}</h4>
                            <small className="text-muted">Durum Değişikliği</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center">
                        <Card.Body>
                            <FaCalculator className="text-success mb-2" size={30} />
                            <h4>{statistics.totalExpenseOperations || 0}</h4>
                            <small className="text-muted">Masraf İşlemi</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center">
                        <Card.Body>
                            <FaUsers className="text-primary mb-2" size={30} />
                            <h4>{statistics.activeUsers || 0}</h4>
                            <small className="text-muted">Aktif Kullanıcı</small>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Error Message */}
            {error && <Alert variant="danger">{error}</Alert>}

            {/* Tabs */}
            <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
                <Card>
                    <Card.Header className="p-0">
                        <Nav variant="tabs" className="border-0">
                            <Nav.Item>
                                <Nav.Link eventKey="price-changes">
                                    <FaMoneyBillWave className="me-2" />
                                    Fiyat Değişiklikleri
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="status-changes">
                                    <FaExchangeAlt className="me-2" />
                                    Durum Değişiklikleri
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="expense-logs">
                                    <FaCalculator className="me-2" />
                                    Masraf İşlemleri
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="user-activity">
                                    <FaUsers className="me-2" />
                                    Kullanıcı Aktivitesi
                                </Nav.Link>
                            </Nav.Item>
                        </Nav>
                    </Card.Header>
                    <Card.Body>
                        <Tab.Content>
                            {/* Price Changes Tab */}
                            <Tab.Pane eventKey="price-changes">
                                {loading ? (
                                    <div className="text-center py-4">
                                        <Spinner animation="border" />
                                        <p className="mt-2">Fiyat değişiklikleri yükleniyor...</p>
                                    </div>
                                ) : logs.length === 0 ? (
                                    <div className="text-center py-4 text-muted">
                                        <FaMoneyBillWave size={40} className="mb-3" />
                                        <p>Seçilen tarih aralığında fiyat değişikliği bulunamadı</p>
                                    </div>
                                ) : (
                                    <Table responsive hover>
                                        <thead>
                                            <tr>
                                                <th>Tarih</th>
                                                <th>Araç</th>
                                                <th>İşlem</th>
                                                <th>Eski Fiyat</th>
                                                <th>Yeni Fiyat</th>
                                                <th>Fark</th>
                                                <th>Kullanıcı</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {logs.map(log => (
                                                <tr key={log._id}>
                                                    <td style={{ fontSize: '0.85em' }}>
                                                        {formatDate(log.timestamp)}
                                                    </td>
                                                    <td>
                                                        <strong>{log.vehicleSnapshot?.plateNumber}</strong>
                                                        <br />
                                                        <small className="text-muted">
                                                            {log.vehicleSnapshot?.brand} {log.vehicleSnapshot?.model}
                                                        </small>
                                                    </td>
                                                    <td>
                                                        {getActionBadge(log.actionType)}
                                                    </td>
                                                    <td>{formatCurrency(log.changes?.oldValue || 0)}</td>
                                                    <td>{formatCurrency(log.changes?.newValue || 0)}</td>
                                                    <td>
                                                        <Badge bg={
                                                            (log.changes?.newValue || 0) > (log.changes?.oldValue || 0) 
                                                                ? 'success' : 'danger'
                                                        }>
                                                            {formatCurrency((log.changes?.newValue || 0) - (log.changes?.oldValue || 0))}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <small>
                                                            {log.userName}
                                                            <br />
                                                            <Badge bg="secondary" size="sm">{log.userType}</Badge>
                                                        </small>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                )}
                            </Tab.Pane>

                            {/* Status Changes Tab */}
                            <Tab.Pane eventKey="status-changes">
                                {loading ? (
                                    <div className="text-center py-4">
                                        <Spinner animation="border" />
                                        <p className="mt-2">Durum değişiklikleri yükleniyor...</p>
                                    </div>
                                ) : logs.length === 0 ? (
                                    <div className="text-center py-4 text-muted">
                                        <FaExchangeAlt size={40} className="mb-3" />
                                        <p>Seçilen tarih aralığında durum değişikliği bulunamadı</p>
                                    </div>
                                ) : (
                                    <Table responsive hover>
                                        <thead>
                                            <tr>
                                                <th>Tarih</th>
                                                <th>Araç</th>
                                                <th>Eski Durum</th>
                                                <th>Yeni Durum</th>
                                                <th>Kullanıcı</th>
                                                <th>Açıklama</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {logs.map(log => (
                                                <tr key={log._id}>
                                                    <td style={{ fontSize: '0.85em' }}>
                                                        {formatDate(log.timestamp)}
                                                    </td>
                                                    <td>
                                                        <strong>{log.vehicleSnapshot?.plateNumber}</strong>
                                                        <br />
                                                        <small className="text-muted">
                                                            {log.vehicleSnapshot?.brand} {log.vehicleSnapshot?.model}
                                                        </small>
                                                    </td>
                                                    <td>
                                                        <Badge bg="secondary">{log.changes?.oldValue}</Badge>
                                                    </td>
                                                    <td>
                                                        <Badge bg="primary">{log.changes?.newValue}</Badge>
                                                    </td>
                                                    <td>
                                                        <small>
                                                            {log.userName}
                                                            <br />
                                                            <Badge bg="secondary" size="sm">{log.userType}</Badge>
                                                        </small>
                                                    </td>
                                                    <td>
                                                        <small className="text-muted">
                                                            {log.changes?.description || 'Durum değişikliği'}
                                                        </small>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                )}
                            </Tab.Pane>

                            {/* Expense Logs Tab */}
                            <Tab.Pane eventKey="expense-logs">
                                {loading ? (
                                    <div className="text-center py-4">
                                        <Spinner animation="border" />
                                        <p className="mt-2">Masraf işlemleri yükleniyor...</p>
                                    </div>
                                ) : logs.length === 0 ? (
                                    <div className="text-center py-4 text-muted">
                                        <FaCalculator size={40} className="mb-3" />
                                        <p>Seçilen tarih aralığında masraf işlemi bulunamadı</p>
                                    </div>
                                ) : (
                                    <Table responsive hover>
                                        <thead>
                                            <tr>
                                                <th>Tarih</th>
                                                <th>Araç</th>
                                                <th>İşlem</th>
                                                <th>Masraf Türü</th>
                                                <th>Tutar</th>
                                                <th>Açıklama</th>
                                                <th>Kullanıcı</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {logs.map(log => (
                                                <tr key={log._id}>
                                                    <td style={{ fontSize: '0.85em' }}>
                                                        {formatDate(log.timestamp)}
                                                    </td>
                                                    <td>
                                                        <strong>{log.vehicleSnapshot?.plateNumber}</strong>
                                                        <br />
                                                        <small className="text-muted">
                                                            {log.vehicleSnapshot?.brand} {log.vehicleSnapshot?.model}
                                                        </small>
                                                    </td>
                                                    <td>
                                                        {getActionBadge(log.actionType)}
                                                    </td>
                                                    <td>
                                                        <Badge bg="info">{log.expenseData?.type || 'N/A'}</Badge>
                                                    </td>
                                                    <td>
                                                        <Badge bg={log.actionType === 'EXPENSE_ADD' ? 'danger' : 'success'}>
                                                            {log.actionType === 'EXPENSE_DELETE' && '-'}
                                                            {formatCurrency(log.amount || 0)}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <small className="text-muted">
                                                            {log.expenseData?.description || 'Açıklama yok'}
                                                        </small>
                                                    </td>
                                                    <td>
                                                        <small>
                                                            {log.userName}
                                                            <br />
                                                            <Badge bg="secondary" size="sm">{log.userType}</Badge>
                                                        </small>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                )}
                            </Tab.Pane>

                            {/* User Activity Tab */}
                            <Tab.Pane eventKey="user-activity">
                                {loading ? (
                                    <div className="text-center py-4">
                                        <Spinner animation="border" />
                                        <p className="mt-2">Kullanıcı aktiviteleri yükleniyor...</p>
                                    </div>
                                ) : logs.length === 0 ? (
                                    <div className="text-center py-4 text-muted">
                                        <FaUsers size={40} className="mb-3" />
                                        <p>Seçilen tarih aralığında kullanıcı aktivitesi bulunamadı</p>
                                    </div>
                                ) : (
                                    <Table responsive hover>
                                        <thead>
                                            <tr>
                                                <th>Tarih</th>
                                                <th>Kullanıcı</th>
                                                <th>İşlem</th>
                                                <th>Araç</th>
                                                <th>Detay</th>
                                                <th>Tutar</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {logs.map(log => (
                                                <tr key={log._id}>
                                                    <td style={{ fontSize: '0.85em' }}>
                                                        {formatDate(log.timestamp)}
                                                    </td>
                                                    <td>
                                                        <strong>{log.userName}</strong>
                                                        <br />
                                                        <Badge bg="secondary" size="sm">{log.userType}</Badge>
                                                    </td>
                                                    <td>
                                                        {getActionBadge(log.actionType)}
                                                    </td>
                                                    <td>
                                                        <strong>{log.vehicleSnapshot?.plateNumber}</strong>
                                                        <br />
                                                        <small className="text-muted">
                                                            {log.vehicleSnapshot?.brand} {log.vehicleSnapshot?.model}
                                                        </small>
                                                    </td>
                                                    <td>
                                                        <small className="text-muted">
                                                            {log.changes?.description || log.expenseData?.description || 'İşlem yapıldı'}
                                                        </small>
                                                    </td>
                                                    <td>
                                                        {log.amount && (
                                                            <Badge bg="warning">
                                                                {formatCurrency(log.amount)}
                                                            </Badge>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                )}
                            </Tab.Pane>
                        </Tab.Content>
                    </Card.Body>
                </Card>
            </Tab.Container>
        </Container>
    );
};

export default AdminLogDashboard;